import { Flex } from '@uhg-abyss/web/ui/Flex';
import { ModalDialog } from '@uhg-abyss/web/ui/ModalDialog';
import { Text } from '@uhg-abyss/web/ui/Text';
import { find } from 'lodash';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useFeatureFlag } from '../../../../hooks/useFeatureFlag';
import { useLagoon } from '../../../../hooks/useLagoon';
import { getYesNoNAText } from '../../../../utils/providerDetails.utils';
import { ConstantsLagoon } from '../../../ConstantsLagoon';
import { ViewMoreButton } from '../ProviderLocation.style';
import {
  SpecialtyOfferedByFacilityModal,
  specialtiesNewOffered,
  specialtiesOfferedTitle,
} from './SpecialtyOfferedByFacility.styled';

export const SpecialtiesOfferedByFacility = ({
  notAvailableOrUnknownText,
  facilityProviderSpecialties,
}) => {
  const { t } = useTranslation();
  const [showAllSpecialties, setShowAllSpecialties] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const handleViewMore = () => {
    if (enableColorCodedSpecialties) {
      setShowAllSpecialties(true);
      // Focus on the 4th list item
      setTimeout(() => {
        const allListItems = document.querySelectorAll('li [role="listitem"]');
        if (allListItems && allListItems[3]) {
          (allListItems[3] as HTMLElement).focus();
        }
      }, 50);
    } else {
      setShowModal(true);
    }
  };

  const handleViewLess = () => {
    setShowAllSpecialties(false);
  };

  const handleCloseModal = () => setShowModal(false);

  const uiMessaging = useLagoon('ui-messaging')();
  const specialtiesOfferedByFacilitiesModal = find(uiMessaging, {
    key: ConstantsLagoon.FACILITY_LOCATIONS.SPECIALTIES_OFFERED_BY_FACILITY,
  });

  const [enableColorCodedSpecialties] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_COLOR_CODED_SPECIALTIES,
  ]);

  const getSpecialties = (facilities) => {
    const allowedKeys = [
      'hasAnesthesiology',
      'hasEmergencyMedicine',
      'hasPathology',
      'hasRadiology',
      'hasNeonatology',
      'hasHospitalist',
      'hasIntensivist',
    ]; // Define the required keys

    return Object.entries(facilities)
      .filter(([key]) => allowedKeys.includes(key))
      .map(([key, value]) => ({
        key: key
          .replace(/has/, '')
          .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
          .toLowerCase(),
        hasSpecialty: value,
      }))
      .sort((a, b) => a.key.localeCompare(b.key));
  };

  const specialties = getSpecialties(facilityProviderSpecialties);
  const displayedSpecialties = showAllSpecialties
    ? specialties
    : specialties.slice(0, 3);

  return (
    <div>
      <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
        {displayedSpecialties.map((specialty, index) => (
          <li key={index}>
            <Flex
              aria-label={`${t(
                `PROVIDER_DETAILS.LOCATIONS_TAB.${specialty.key.toUpperCase()}`
              )} - ${
                getYesNoNAText(t, specialty.hasSpecialty as boolean) ||
                notAvailableOrUnknownText
              }`}
              css={{ gap: '4px' }}
              data-testid={`sub-heading-speciality-${specialty.key.replace(
                /_/g,
                '-'
              )}`}
              role="listitem"
              style={{ outline: 'none' }}
              tabIndex={-1}
            >
              <Text css={specialtiesOfferedTitle}>
                {t(
                  `PROVIDER_DETAILS.LOCATIONS_TAB.${specialty.key.toUpperCase()}`
                )}{' '}
                -{' '}
              </Text>
              <Text
                css={specialtiesNewOffered(
                  getYesNoNAText(t, specialty.hasSpecialty as boolean),
                  enableColorCodedSpecialties,
                  t
                )}
              >
                {getYesNoNAText(t, specialty.hasSpecialty as boolean) ||
                  notAvailableOrUnknownText}
              </Text>
            </Flex>
          </li>
        ))}
      </ul>

      {specialties.length > 3 && (
        <>
          {!showAllSpecialties ? (
            <ViewMoreButton
              data-testid="view-more-specialty-offered"
              onClick={handleViewMore}
            >
              <Text color="$core.color.brand.60" fontSize="md">
                {`+${specialties.length - 3}  ${t('More')}`}
              </Text>
            </ViewMoreButton>
          ) : (
            <ViewMoreButton
              data-testid="view-less-specialty-offered"
              onClick={handleViewLess}
            >
              <Text color="$core.color.brand.60" fontSize="md">
                {t('View less')}
              </Text>
            </ViewMoreButton>
          )}
        </>
      )}

      <ModalDialog
        css={SpecialtyOfferedByFacilityModal}
        data-testid="specialty-offered-modal"
        isOpen={showModal}
        onClose={handleCloseModal}
        title={specialtiesOfferedByFacilitiesModal?.message}
      >
        <div>
          {specialties.map((specialty, index) => (
            <Flex
              css={{ gap: '4px' }}
              data-testid={`sub-heading-speciality-modal-${specialty.key.replace(
                /_/g,
                '-'
              )}`}
              key={index}
            >
              <Text css={specialtiesOfferedTitle}>
                {t(
                  `PROVIDER_DETAILS.LOCATIONS_TAB.${specialty.key.toUpperCase()}`
                )}{' '}
                -{' '}
              </Text>
              <Text
                css={specialtiesNewOffered(
                  getYesNoNAText(t, specialty.hasSpecialty as boolean),
                  enableColorCodedSpecialties,
                  t
                )}
              >
                {getYesNoNAText(t, specialty.hasSpecialty as boolean) ||
                  notAvailableOrUnknownText}
              </Text>
            </Flex>
          ))}
        </div>
      </ModalDialog>
    </div>
  );
};
